import type {
	ReactNode,
	ReactSVGElement,
	ReactElement,
	PropsWithChildren,
	JSX,
} from 'react'
import {useState, useEffect, useRef, createRef, cloneElement} from 'react'
import dropdownStyles from './countryDropdown.module.css'

type Flag = ReactSVGElement | ReactElement<{className?: string}>
type DropdownOpenDirection = 'up' | 'down'

interface SecondaryDropdownCountry {
	label: string
	url: string
}

interface DropdownCountry {
	flag: Flag
	mainUrl: string
	name: string
	secondaryUrls: SecondaryDropdownCountry[]
}

interface DropdownCountryData {
	data: DropdownCountry[]
}

interface CountryDropdownProps {
	activeCountryOption?: string
	className?: string
	defaultSelectedValue?: string
	dropdownCountryData: DropdownCountryData | null
	dropdownLabel?: string
	dropdownMenuClasses?: string
	dropdownYOffset?: number
	onChange?: ((data: DropdownCountry, url: string) => void) | null
	showNextBtnText?: string
	showPrevBtnText?: string
}

export const CountryDropdown = ({
	activeCountryOption = '',
	defaultSelectedValue = 'Select a country',
	dropdownLabel = 'Country selector',
	dropdownMenuClasses = '',
	dropdownCountryData,
	onChange = null,
	children = null,
	className = '',
	dropdownYOffset = 58,
	showNextBtnText = 'Show next menu',
	showPrevBtnText = 'Show previous menu',
}: PropsWithChildren<CountryDropdownProps>): JSX.Element => {
	const hasInit = useRef<boolean>(false)
	let navTimer: number | NodeJS.Timeout = 0
	const [dropdownOffset, setDropdownOffset] = useState(0)
	const [dropdownOpenDirection, setDropdownOpenDirection] =
		useState<DropdownOpenDirection>('down')
	const [dropdownText, setDropdownText] =
		useState<ReactNode>(defaultSelectedValue)
	const dropdownMenuRef = createRef<HTMLUListElement>()
	const dropdownButtonRef = createRef<HTMLButtonElement>()
	const dropdownMenuContainerRef = createRef<HTMLDivElement>()

	const createFlagEl = (flag: Flag): ReactNode =>
		cloneElement(flag, {
			className: `${dropdownStyles.CountryDropdownMenuFlag}` || '',
		})

	const setDropdown = (text: string, flag: Flag): void => {
		setDropdownText(
			<span className="flex items-center">
				{createFlagEl(flag)} {text}
			</span>
		)
	}

	const calculateOpenDirection = (): DropdownOpenDirection => {
		const dropdownContainerEl = dropdownMenuContainerRef.current
		const dropdownEl = dropdownMenuRef.current
		if (
			dropdownEl &&
			document.body.clientHeight < dropdownEl.offsetHeight
		) {
			// This mostly is for stories where the whole document is very thin
			setDropdownOpenDirection('down')
			return 'down'
		}
		if (
			dropdownContainerEl &&
			dropdownEl &&
			dropdownContainerEl.offsetTop + dropdownEl.offsetHeight >
				document.body.clientHeight
		) {
			// The dropdown would overflow the bottom, so display upwards
			setDropdownOpenDirection('up')
			return 'up'
		}
		setDropdownOpenDirection('down')
		return 'down'
	}

	const calcDropdownOffset = (): void => {
		const dropdownEl = dropdownMenuRef.current

		if (!dropdownEl) return

		if (document.body.clientWidth < dropdownEl.offsetWidth) {
			dropdownEl.classList.add('fixed')
			setDropdownOffset(0)
			return
		}
		dropdownEl.classList.remove('fixed')
		const dropdownContainerEl = dropdownMenuContainerRef.current
		if (
			dropdownContainerEl &&
			dropdownButtonRef.current &&
			dropdownContainerEl.offsetLeft + dropdownEl.offsetWidth >
				document.body.clientWidth
		) {
			const dropdownButtonEl = dropdownButtonRef.current

			setDropdownOffset(
				-(dropdownEl.offsetWidth - dropdownButtonEl.offsetWidth)
			)
			return
		}

		setDropdownOffset(0)
	}

	const dropdownClick = (): void => {
		const dropdownEl = dropdownMenuRef.current

		if (!dropdownEl) return

		if (
			dropdownStyles.Open &&
			dropdownEl.classList.contains(dropdownStyles.Open)
		) {
			if (dropdownStyles.Open) {
				dropdownEl.classList.remove(dropdownStyles.Open)
			}
			const classToRemove = dropdownStyles[dropdownOpenDirection]
			if (classToRemove) {
				dropdownEl.classList.remove(classToRemove)
			}
		} else {
			if (dropdownStyles.Open) {
				dropdownEl.classList.add(dropdownStyles.Open)
			}
			const directionClass = dropdownStyles[calculateOpenDirection()]
			if (directionClass) {
				dropdownEl.classList.add(directionClass)
			}
			calcDropdownOffset()
			dropdownEl.focus()
		}
	}

	const hideDropdown = (shouldAlwaysHide: boolean): void => {
		const dropdownEl = dropdownMenuRef.current
		if (
			dropdownEl === null ||
			!dropdownStyles.Open ||
			!dropdownEl.classList.contains(dropdownStyles.Open)
		) {
			return
		}
		if (shouldAlwaysHide || !dropdownEl.contains(document.activeElement)) {
			if (dropdownStyles.Open) {
				dropdownEl.classList.remove(dropdownStyles.Open)
			}
			const classToRemove = dropdownStyles[dropdownOpenDirection]
			if (classToRemove) {
				dropdownEl.classList.remove(classToRemove)
			}
		}
	}

	const insideEvent = (): void => {
		clearTimeout(navTimer)
	}

	const outsideEvent = (): void => {
		navTimer = setTimeout(() => {
			hideDropdown(false)
		}, 0)
	}

	const showNextMenu: React.MouseEventHandler<HTMLButtonElement> = (e) => {
		const btn = e.target as HTMLButtonElement
		const li = btn.closest('li')

		if (!li || !dropdownStyles.CountryDropdownMenuItemActive) return

		li.classList.add(dropdownStyles.CountryDropdownMenuItemActive)
		setTimeout(() => {
			li
				.querySelector(
					`.${dropdownStyles.CountryDropdownMenuSecondLevel || ''}`
				)
				?.querySelector<HTMLAnchorElement>('button, a')
				?.focus()
		}, 400)
	}

	const showPrevMenu: React.MouseEventHandler<HTMLButtonElement> = (e) => {
		const btn = e.target as HTMLButtonElement
		const li = btn.closest('li')

		if (!li || !dropdownStyles.CountryDropdownMenuItemActive) return

		li.classList.remove(dropdownStyles.CountryDropdownMenuItemActive)
		li
			.querySelector(
				`.${dropdownStyles.CountryDropdownMenuFirstLevel || ''}`
			)
			?.querySelector<HTMLAnchorElement>('button, a')
			?.focus()
	}

	const keyboardEvent = (e: KeyboardEvent): void => {
		if (e.keyCode === 27) {
			// Esc key was pressed
			const dropdownEl = dropdownMenuRef.current
			if (
				dropdownEl === null ||
				!dropdownStyles.Open ||
				!dropdownEl.classList.contains(dropdownStyles.Open)
			) {
				return
			}
			if (dropdownStyles.Open) {
				dropdownEl.classList.remove(dropdownStyles.Open)
			}
			const classToRemove = dropdownStyles[dropdownOpenDirection]
			if (classToRemove) {
				dropdownEl.classList.remove(classToRemove)
			}

			dropdownButtonRef.current?.focus()
		}
	}

	useEffect(() => {
		if (!hasInit.current && activeCountryOption) {
			hasInit.current = true
			const activeCountryData = dropdownCountryData?.data.find(
				(x) =>
					x.name.toLowerCase() === activeCountryOption.toLowerCase()
			)
			if (activeCountryData !== undefined) {
				setDropdown(activeCountryOption, activeCountryData.flag)
			}
		}
		const dropdownContainerEl = dropdownMenuContainerRef.current
		if (dropdownContainerEl === null) {
			return
		}
		document.addEventListener('keydown', keyboardEvent)
		dropdownContainerEl.addEventListener('focus', insideEvent, true)
		dropdownContainerEl.addEventListener('blur', outsideEvent, true)

		return () => {
			dropdownContainerEl.removeEventListener('focus', insideEvent, true)
			dropdownContainerEl.removeEventListener('blur', outsideEvent, true)
			document.removeEventListener('keydown', keyboardEvent)
		}
	})

	const onChangeEv = (data: DropdownCountry, url: string): void => {
		hideDropdown(true)
		setDropdown(data.name, data.flag)
		if (onChange) {
			onChange(data, url)
		}
	}

	return (
		<div className={`relative ${className}`} ref={dropdownMenuContainerRef}>
			{dropdownLabel ? (
				<span className="label-text top">{dropdownLabel}</span>
			) : null}
			<button
				className={children ? '' : 'dropdown dropdown-blue-icon'}
				onClick={dropdownClick}
				ref={dropdownButtonRef}
				type="button"
			>
				{children ? children : null}
				{!children && dropdownText}
			</button>
			{!children && (
				<svg
					aria-hidden="true"
					className="pointer-events-none"
					focusable="false"
					height="46"
					viewBox="0 0 48 48"
					width="46"
					xmlns="http://www.w3.org/2000/svg"
				>
					<rect fill="" height="48" rx="24" width="48" />
					<path
						d="M7,10,18.426,21.426,29.853,10Z"
						fill="#fff"
						fillRule="evenodd"
						transform="translate(5.574 10.287)"
					/>
				</svg>
			)}
			<ul
				className={`absolute left-0 hidden max-h-[300px] w-full min-w-[300px] overflow-y-auto border border-gray-500 md:min-w-[400px] ${
					dropdownStyles.CountryDropdownMenu || ''
				} ${dropdownMenuClasses}`}
				ref={dropdownMenuRef}
				style={{
					bottom:
						dropdownOpenDirection === 'up'
							? `${dropdownYOffset}px`
							: 'auto',
					left: `${dropdownOffset}px`,
					top:
						dropdownOpenDirection === 'down'
							? `${dropdownYOffset}px`
							: 'auto',
				}}
				tabIndex={-1}
			>
				{dropdownCountryData?.data.map((data) => (
					<li className="relative" key={data.name}>
						<span
							className={
								dropdownStyles.CountryDropdownMenuFirstLevel ||
								''
							}
						>
							<button
								className="flex w-4/5 items-center p-4 hover:underline focus:underline md:w-full"
								onClick={() => {
									onChangeEv(data, data.mainUrl)
								}}
								type="button"
							>
								{createFlagEl(data.flag)}
								<span>{data.name}</span>
							</button>
							<button
								aria-label={showNextBtnText}
								className="flex w-1/5 items-center justify-center bg-gray-200 md:hidden"
								onClick={showNextMenu}
								type="button"
							>
								<svg
									aria-hidden="true"
									className="pointer-events-none h-5 w-5"
									focusable="false"
									height="47.24"
									viewBox="0 0 48.46 47.24"
									width="48.46"
								>
									<path
										d="M20.61,3.16,23,.76a2.59,2.59,0,0,1,3.65,0h0l21,21a2.59,2.59,0,0,1,0,3.66h0l-21,21a2.6,2.6,0,0,1-3.66,0h0l-2.4-2.4a2.6,2.6,0,0,1,0-3.68l0,0L33.69,28H2.6A2.6,2.6,0,0,1,0,25.37V21.89a2.58,2.58,0,0,1,2.58-2.6H33.69l-13-12.41a2.59,2.59,0,0,1-.11-3.65A.46.46,0,0,0,20.61,3.16Z"
										style={{fill: '#02253E'}}
										transform="translate(0 0)"
									/>
								</svg>
							</button>
						</span>
						<span
							className={
								dropdownStyles.CountryDropdownMenuSecondLevel ||
								''
							}
						>
							<span
								className={
									dropdownStyles.CountryDropdownMenuSecondLevelLinksContainer ||
									''
								}
							>
								{data.secondaryUrls.map((secondaryUrl) => (
									<button
										className={
											dropdownStyles.CountryDropdownMenuSecondaryLink ||
											''
										}
										key={secondaryUrl.label}
										onClick={() => {
											onChangeEv(data, secondaryUrl.url)
										}}
										type="button"
									>
										<span>{secondaryUrl.label}</span>
										<svg
											aria-hidden="true"
											focusable="false"
											height="10.927"
											width="10.932"
											xmlns="http://www.w3.org/2000/svg"
										>
											<g fill="#02253e">
												<path d="M5.791 5.511a.419.419 0 00.3-.123l3.954-3.954v2.044a.42.42 0 10.84 0V.42a.42.42 0 00-.42-.42H7.404a.42.42 0 100 .84h2.044L5.463 4.794a.42.42 0 00.3.717z" />
												<path d="M10.507 5.914a.42.42 0 00-.42.42v3.472a.274.274 0 01-.274.274H1.12a.274.274 0 01-.274-.274V1.115A.274.274 0 011.12.84h3.288a.42.42 0 000-.84H1.12A1.12 1.12 0 000 1.12v8.687a1.12 1.12 0 001.12 1.12h8.692a1.12 1.12 0 001.12-1.12V6.334a.42.42 0 00-.42-.42z" />
											</g>
										</svg>
									</button>
								))}
							</span>
							<button
								aria-label={showPrevBtnText}
								className="flex w-1/5 items-center justify-center bg-gray-200 md:hidden"
								onClick={showPrevMenu}
								type="button"
							>
								<svg
									aria-hidden="true"
									className="pointer-events-none h-5 w-5"
									focusable="false"
									height="21.5"
									viewBox="0 0 21.5 21.5"
									width="21.5"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path
										d="M2.27,21.21l-2-2a1,1,0,0,1,0-1.41h0l7.07-7.07L.29,3.68a1,1,0,0,1,0-1.41l2-2a1,1,0,0,1,1.41,0l7.07,7.07L17.82.29a1,1,0,0,1,1.41,0l2,2a1,1,0,0,1,0,1.41l-7.07,7.07,7.07,7.07a1,1,0,0,1,0,1.41h0l-2,2a1,1,0,0,1-1.41,0l-7.07-7.07L3.68,21.21a1,1,0,0,1-1.41,0Z"
										style={{fill: '#02253E'}}
										transform="translate(0 0)"
									/>
								</svg>
							</button>
						</span>
					</li>
				))}
			</ul>
		</div>
	)
}
